/**
 * File: ReadyToSendCard.tsx
 * Path: apps/admin/src/shared/components/ReadyToSendCard.tsx
 * Description: Ready to send, on Money and Today: invoices drafted for Liam
 * (care plan months a week before they are due), each with one Send
 * button, and why a month waits for him when it does. Shows nothing when
 * no draft waits.
 */

import React from "react";
import { Link } from "react-router-dom";
import { formatCents } from "@idk/config";
import { Button } from "@idk/ui";
import { Box, Pill } from "./BooksPrimitive";
import { formatDay } from "../utils/pipelineViewUtils";
import { useReadyToSendController } from "../hooks/useReadyToSendController";

export const ReadyToSendCard: React.FC<{ className?: string }> = ({ className }) => {
  const { rows, pending, hrefOf, send } = useReadyToSendController();
  if (rows.length === 0) return null;
  return (
    <Box title="Ready to send" caption="Invoices drafted for you before they are due. Check each one: Send gives it its number and emails it to the client with the PDF." className={className}>
      <ul aria-label="Ready to send" className="divide-y divide-border/40 text-sm">
        {rows.map((r) => (
          <li key={r.invoiceId} className="flex flex-wrap items-center justify-between gap-stack-sm py-stack-xs">
            <div className="min-w-0 space-y-stack-xs">
              <Link to={hrefOf(r.invoiceId)} className="inline-flex min-h-11 items-center font-bold hover:text-primary hover:underline">
                {r.party}
              </Link>
              <p className="text-xs text-muted-foreground">
                {r.forText ? `${r.forText} · ` : ""}
                {formatCents(r.totalCents)} · due {formatDay(r.dueOn)}
              </p>
              {r.holds.length > 0 && (
                <div className="flex flex-wrap items-start gap-stack-xs">
                  <Pill tone="amber">Check first</Pill>
                  <span className="text-xs text-foreground">{r.holds.join(" ")}</span>
                </div>
              )}
            </div>
            <Button type="button" className="h-11 rounded-none" disabled={pending} onClick={() => send(r.invoiceId)}>
              Send
              <span className="sr-only"> the invoice to {r.party}</span>
            </Button>
          </li>
        ))}
      </ul>
    </Box>
  );
};
